<template>
    <div class="com-container">
        <div class="com-chart" ref="realTIme_ref"></div>
    </div>
</template>

<script>
import Vue from 'vue'

export default {
    name: 'TemperatureChart',
    data() {
        return {
            chartInstance: null,
            allData: null,
            timerId: null,
            // 颜色配置，支持多个传感器
            colorConfig: [
                "rgb(255, 70, 131)",   // 红色系
                "rgb(70, 131, 255)",   // 蓝色系
                "rgb(70, 255, 131)",   // 绿色系
                "rgb(255, 255, 70)",   // 黄色系
                "rgb(131, 70, 255)",   // 紫色系
                "rgb(255, 131, 70)",   // 橙色系
                "rgb(70, 255, 255)",   // 青色系
                "rgb(255, 70, 255)",   // 粉色系
                "rgb(131, 255, 70)",   // 浅绿色
                "rgb(70, 131, 255)"    // 深蓝色
            ]
        };
    },
    mounted() {
        this.initChart();
        this.startInterval();
        this.getData();
        window.addEventListener("resize", this.screenAdapter);
        this.screenAdapter();
    },
    destroyed() {
        clearInterval(this.timerId);
        window.removeEventListener("resize", this.screenAdapter);
    },
    methods: {
        initChart() {
            this.chartInstance = this.$echarts.init(this.$refs.realTIme_ref, "dark");
            
            const initOption = {
                title: {
                    text: " ▎病房温度分析",
                    left: 20,
                    top: 20,
                },
                grid: {
                    top: "35%",
                    left: "3%",
                    right: "4%",
                    bottom: "5%",
                    containLabel: true,
                },
                tooltip: {
                    trigger: "axis",
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    borderWidth: '0.1',
                    textStyle: {
                        color: 'black'
                    },
                    valueFormatter: function (value) {
                        return value + '℃'
                    }
                },
                legend: {
                    left: 40,
                    top: '20%',
                    icon: 'circle',
                    data: [] // 初始为空，动态更新
                },
                dataZoom: [
                    {
                        type: "inside",
                        xAxisIndex: 0,
                    },
                    {
                        start: 0,
                        end: 100,
                    },
                ],
                xAxis: {
                    type: "category",
                    boundaryGap: false,
                    data: [] // 初始为空，动态更新
                },
                yAxis: {
                    type: "value",
                    max: 60,
                    min: -10,
                    boundaryGap: [0, "100%"],
                    axisLine: {
                        show: true,
                    },
                    axisLabel: {
                        formatter: "{value} °C",
                    },
                },
                series: [] // 初始为空，动态更新
            };
            
            this.chartInstance.setOption(initOption);
        },
        
        startInterval() {
            if (this.timerId) {
                clearInterval(this.timerId);
            }
            this.timerId = setInterval(() => {
                this.getData();
            }, 1000);
        },
        
        async getData() {
            try {
                const ret = await this.$http.get('sensor/getFiveMinData');
                this.allData = ret.data;
                this.updateChart();
            } catch (error) {
                console.error('获取数据失败:', error);
                // 可以添加错误提示，但不要中断图表显示
            }
        },
        
        updateChart() {
            // 检查数据有效性
            if (!this.allData || !Array.isArray(this.allData) || this.allData.length === 0) {
                console.warn('无有效数据');
                // 可以显示空图表或提示信息
                return;
            }
            
            // 处理数据：提取时间轴和传感器数据
            const { timeData, sensorDataMap, sensorNames } = this.processSensorData(this.allData);
            
            // 动态生成系列配置
            const series = [];
            const legendData = [];
            
            // 为每个传感器创建系列
            Object.keys(sensorDataMap).forEach((sensorId, index) => {
                const sensorName = sensorNames[sensorId];
                const displayName = this.getDisplayName(sensorName); // 获取显示名称（前三个字）
                const seriesColor = this.colorConfig[index % this.colorConfig.length];
                
                series.push({
                    name: displayName,
                    type: "line",
                    smooth: true,
                    symbol: "none",
                    sampling: "lttb",
                    markPoint: {
                        data: [
                            { type: "max", name: "Max" },
                            { type: "min", name: "Min" },
                        ],
                    },
                    itemStyle: {
                        color: seriesColor,
                    },
                    areaStyle: {
                        color: this.createGradientColor(seriesColor),
                    },
                    data: sensorDataMap[sensorId]
                });
                
                legendData.push(displayName);
            });
            
            const dataOption = {
                xAxis: {
                    data: timeData,
                },
                series: series,
                legend: {
                    data: legendData
                }
            };
            
            // 使用notMerge: false确保完全替换series
            this.chartInstance.setOption(dataOption, { notMerge: false });
        },
        
        // 处理传感器数据，按传感器ID分组
        processSensorData(allData) {
            const timeData = []; // 时间轴数据
            const sensorDataMap = {}; // 传感器ID -> 温度数据数组
            const sensorNames = {}; // 传感器ID -> 传感器名称
            
            // 遍历每个时间点的数据
            allData.forEach((timePointData, timeIndex) => {
                // 获取时间（使用第一个传感器的时间）
                if (Array.isArray(timePointData) && timePointData.length > 0) {
                    const firstSensor = timePointData[0];
                    if (firstSensor && firstSensor.sTime) {
                        timeData.push(firstSensor.sTime.substring(11, 19));
                    } else {
                        timeData.push(`时间${timeIndex + 1}`);
                    }
                    
                    // 处理每个传感器的数据
                    timePointData.forEach(sensor => {
                        if (sensor && sensor.sId !== undefined) {
                            const sensorId = sensor.sId;
                            
                            // 初始化传感器数据数组（如果尚未初始化）
                            if (!sensorDataMap[sensorId]) {
                                sensorDataMap[sensorId] = new Array(allData.length).fill(null);
                            }
                            
                            // 存储传感器名称（只存储一次）
                            if (!sensorNames[sensorId] && sensor.sName) {
                                sensorNames[sensorId] = sensor.sName;
                            }
                            
                            // 在对应时间点位置存储温度数据
                            sensorDataMap[sensorId][timeIndex] = sensor.sTemperature;
                        }
                    });
                } else {
                    // 如果这个时间点没有数据，添加占位时间
                    timeData.push(`时间${timeIndex + 1}`);
                }
            });
            
            // 确保所有传感器数据数组长度与时间点数量一致
            Object.keys(sensorDataMap).forEach(sensorId => {
                while (sensorDataMap[sensorId].length < allData.length) {
                    sensorDataMap[sensorId].push(null);
                }
            });
            
            return { timeData, sensorDataMap, sensorNames };
        },
        
        // 获取显示名称（取传感器名称的前三个字）
        getDisplayName(sensorName) {
            if (!sensorName || typeof sensorName !== 'string') {
                return '未知传感器';
            }
            
            // 去除空格和特殊字符，然后取前三个字
            const cleanName = sensorName.replace(/\s+/g, '');
            
            if (cleanName.length <= 3) {
                return cleanName;
            } else {
                return cleanName.substring(0, 3);
            }
        },
        
        // 创建渐变颜色
        createGradientColor(baseColor) {
            const rgbaStart = this.convertRgbToRgba(baseColor, 0.5);
            const rgbaEnd = this.convertRgbToRgba(baseColor, 0);
            
            return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                    offset: 0,
                    color: rgbaStart,
                },
                {
                    offset: 1,
                    color: rgbaEnd,
                },
            ]);
        },
        
        // 将rgb颜色转换为rgba颜色
        convertRgbToRgba(rgbColor, alpha) {
            if (rgbColor.startsWith('rgb(')) {
                return rgbColor.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
            }
            // 如果已经是rgba格式，直接调整alpha值
            if (rgbColor.startsWith('rgba(')) {
                const parts = rgbColor.match(/rgba$$(\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)$$/);
                if (parts) {
                    return `rgba(${parts[1]}, ${parts[2]}, ${parts[3]}, ${alpha})`;
                }
            }
            // 默认返回
            return rgbColor;
        },
        
        screenAdapter() {
            const titleFontSize = (this.$refs.realTIme_ref.offsetWidth / 100) * 3.6;
            const adapterOption = {
                title: {
                    textStyle: {
                        fontSize: titleFontSize / 1.4,
                    },
                },
                legend: {
                    itemWidth: titleFontSize / 2,
                    itemHeight: titleFontSize / 2,
                    itemGap: titleFontSize / 2,
                    textStyle: {
                        fontSize: titleFontSize / 2
                    }
                }
            };
            this.chartInstance.setOption(adapterOption);
            this.chartInstance.resize();
        }
    }
};
</script>

<style lang="less" scoped>
.com-container {
    width: 100%;
    height: 100%;
    position: relative;
    
    .com-chart {
        width: 100%;
        height: 100%;
    }
}
</style>